import { Injectable, inject } from '@angular/core';
import { Observable, defer, from, of, firstValueFrom } from 'rxjs';
import { concatMap, shareReplay } from 'rxjs/operators';
import { SHEET_KEYS } from '../../models/sheet-keys';
import {
  ConversionPhaseEnum,
  MbrsStatusEnum,
  isPhase1,
  isPhase2,
} from '../../../types/commons';
import {
  FilingFormState,
  SheetLevelStatusData,
  SheetLevelStatusMap,
} from './filing-form-state.service';
import { ConversionValidationCacheService } from './conversion-validation-cache.service';
import { PureValidatorRegistry } from './pure-validator-registry.service';
import { RetrieveMbrsDataService } from './retrieve-mbrs-data.service';
import { SheetValidationContext } from './sheet-validator';
import { buildSheetValidationContext } from './sheet-validation-context.util';
import { XbrlDataTransformerService } from './xbrl-data-transformer.service';
import { FilingFormPersistenceService } from './filing-form-persistence.service';
import { buildSlugToStateKey } from '../../models/sheet-descriptors';
import { SheetApplicabilityService } from './sheet-applicability.service';
import { SHEET_TYPE_BY_STATE_KEY, SheetOption, SheetType } from '../../models/sheet-types';
import { DEI } from '../../constants/qnames';

/**
 * How a conversion's validation result was produced.
 *
 *  - 'in-progress' : Backend reports the conversion is still being
 *                    processed; we deliberately do not validate, per the
 *                    Phase 1 product rule "If the conversion's status is
 *                    In Progress, we need not do any validations".
 *  - 'cache'       : Returned from the per-conversion validation cache
 *                    (entry was complete:true).
 *  - 'computed'    : Cache miss; orchestrator fetched the conversion's
 *                    data, ran the pure-validator registry over every
 *                    sheet, and persisted the result.
 *  - 'phase1-deferred' : The conversion is in Phase 1, where only the Filing
 *                    Prerequisites sheet is revealed (see getEnabledSheets()
 *                    in filing-form-container — Phase 1 exposes nothing else).
 *                    We deliberately validate ONLY `filingPrerequisites` and
 *                    defer the full 25-sheet pass until Phase 2, when those
 *                    sheets actually carry data. Validating them in Phase 1
 *                    flagged every empty-but-mandatory downstream sheet as a
 *                    false `hasErrors: true`. The returned `statuses` map
 *                    therefore contains a single key.
 *
 * Phase 2F removed the legacy 'pending-phase2' sentinel — the registry now
 * covers all 26 sheets unconditionally, so cache-miss compute always lands
 * a real result.
 */
export type ConversionValidationSource =
  | 'in-progress'
  | 'cache'
  | 'computed'
  | 'phase1-deferred';

export interface ConversionValidationResult {
  conversionId: string;
  source: ConversionValidationSource;
  /**
   * Per-sheet validation statuses. Null when `source` is 'in-progress' (no
   * validation attempted). For 'phase1-deferred' the map holds only
   * `filingPrerequisites`.
   */
  statuses: SheetLevelStatusMap | null;
}

/**
 * Translation table: backend slug (as emitted by RetrieveMbrsDataService) ->
 * FilingFormState key. The fetch service keys its response by slug; the
 * validators and cache key by FilingFormState key. Centralising the map here
 * keeps the orchestrator the only owner of the conversion.
 *
 * Sheets absent from this map (filingPrerequisites and the *Liquidity /
 * ByNature / BeforeTax / DirectMethod variants) either come from the
 * persisted modified-state blob rather than the MBRS fetch
 * (filingPrerequisites) or are
 * alternative-layout sheets that the backend only emits for matching
 * submission types. Validators must tolerate an empty payload for any sheet
 * not present in the loaded conversion data.
 */
const SLUG_TO_STATE_KEY: Readonly<Record<string, keyof FilingFormState>> = buildSlugToStateKey();

/**
 * Scope-of-filing method qnames that drive sheet applicability (which layout
 * variant of each statement applies). These are `ssmt:` (not `ssmt-dei:`)
 * concepts and have no entry in the DEI qname table, so they are pinned here as
 * literals — matching the editor's getEnabledSheets() reads verbatim. Used only
 * by applyApplicabilityFilter to feed SheetApplicabilityService.
 */
const METHOD_FINANCIAL_POSITION_KEY = 'ssmt:MethodUsedForPreparingStatementOfFinancialPosition';
const METHOD_PROFIT_LOSS_KEY = 'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss';
const METHOD_COMPREHENSIVE_INCOME_KEY = 'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome';
const METHOD_CASH_FLOWS_KEY = 'ssmt:MethodUsedForPreparingStatementOfCashFlows';

/**
 * FilingFormState key -> role number, used to drive
 * `XbrlDataTransformerService.transformRoleData(roleNumber, slice)` on the
 * headless path so the orchestrator hands validators the SAME flat
 * (`qname[Dimension][Period]`) shape the mounted editor produces.
 *
 * Why this exists: the retrieve-mbrs-data endpoint returns dual-column sheets
 * in the BACKEND DTO shape — bare qname keyed to a nested
 * `{sepCur, sepPrev, conCur, conPrev}` object. The editor flattens that at
 * mount (one `transformRoleData(<role>, slice)` call per `populateFromMbrsData`
 * in filing-form-container). The headless path historically skipped the
 * transform and fed validators the raw nested slice, so every dual-column
 * lookup missed → required fields read empty → false `hasErrors` on every
 * dual-column sheet (role 8/12/18/19/23/24 + role-2 scope), for BOTH
 * taxonomies. See conversion-validator.service.spec.ts.
 *
 * Mirrors the role numbers the editor hard-codes in its `transformRoleData`
 * calls AND the registry in xbrl-data-transformer.service.ts's
 * `roleTransformationMap`. `filingPrerequisites` (role 0) is intentionally
 * absent: it is read from localStorage already in flat form shape (see
 * loadConversionSheetData), and role 0 is a passthrough transform anyway.
 *
 * IMPORTANT: only ever apply this to the RAW fetched (nested) slice. Running
 * a dimensional transform over already-flat data would DROP it — the
 * transformers skip any entry whose value is not a nested object.
 */
const STATE_KEY_TO_ROLE_NUMBER: Readonly<
  Partial<Record<keyof FilingFormState, number>>
> = {
  [SHEET_KEYS.filingInfo]: 1,
  [SHEET_KEYS.scopeOfFiling]: 2,
  [SHEET_KEYS.stockExchangeDisclosure]: 3,
  [SHEET_KEYS.directorsReport]: 4,
  [SHEET_KEYS.statementByDirectors]: 5,
  [SHEET_KEYS.directorBusinessReview]: 6,
  [SHEET_KEYS.auditorsReport]: 7,
  [SHEET_KEYS.statementOfFinancialPosition]: 8,
  [SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity]: 9,
  [SHEET_KEYS.statementOfFinancialPositionLiquidity]: 10,
  [SHEET_KEYS.subClassificationLiquidity]: 11,
  [SHEET_KEYS.statementOfProfitOrLoss]: 12,
  [SHEET_KEYS.analysisOfProfitOrLoss]: 13,
  [SHEET_KEYS.statementOfProfitOrLossByNature]: 14,
  [SHEET_KEYS.statementOfComprehensiveIncome]: 15,
  [SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax]: 16,
  [SHEET_KEYS.statementOfCashFlowsDirectMethod]: 17,
  [SHEET_KEYS.statementOfCashFlowsIndirectMethod]: 18,
  [SHEET_KEYS.statementOfChangesInEquity]: 19,
  [SHEET_KEYS.notesCorporateInformation]: 20,
  [SHEET_KEYS.notesAccountingPolicies]: 21,
  [SHEET_KEYS.notesListOfNotes]: 22,
  [SHEET_KEYS.notesIssuedCapital]: 23,
  [SHEET_KEYS.notesRelatedPartyTransactions]: 24,
};

/**
 * Full set of sheet keys the orchestrator must produce a status for. Drives
 * the registry-coverage gate: the orchestrator only emits 'computed' when
 * every key here has a registered SheetValidator. While any key is missing,
 * cache-miss requests yield 'pending-phase2' so listings render skeletons
 * rather than partial results that might disagree with the editor.
 *
 * Kept as a flat constant (instead of derived from FilingFormState's
 * TypeScript type at runtime) so accidental additions to the interface
 * surface as a coverage gap rather than a silent expansion.
 */
const ALL_SHEET_KEYS: ReadonlyArray<keyof FilingFormState> = [
  SHEET_KEYS.filingPrerequisites,
  SHEET_KEYS.filingInfo,
  SHEET_KEYS.scopeOfFiling,
  SHEET_KEYS.stockExchangeDisclosure,
  SHEET_KEYS.directorsReport,
  SHEET_KEYS.statementByDirectors,
  SHEET_KEYS.directorBusinessReview,
  SHEET_KEYS.auditorsReport,
  SHEET_KEYS.statementOfFinancialPosition,
  SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
  SHEET_KEYS.statementOfFinancialPositionLiquidity,
  SHEET_KEYS.subClassificationLiquidity,
  SHEET_KEYS.statementOfProfitOrLoss,
  SHEET_KEYS.analysisOfProfitOrLoss,
  SHEET_KEYS.statementOfProfitOrLossByNature,
  SHEET_KEYS.analysisOfProfitOrLossByNature,
  SHEET_KEYS.statementOfComprehensiveIncome,
  SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
  SHEET_KEYS.statementOfCashFlowsDirectMethod,
  SHEET_KEYS.statementOfCashFlowsIndirectMethod,
  SHEET_KEYS.statementOfChangesInEquity,
  SHEET_KEYS.notesCorporateInformation,
  SHEET_KEYS.notesAccountingPolicies,
  SHEET_KEYS.notesListOfNotes,
  SHEET_KEYS.notesIssuedCapital,
  SHEET_KEYS.notesRelatedPartyTransactions,
];

/**
 * Sheets the orchestrator validates while a conversion is in Phase 1.
 *
 * Phase 1 reveals only the Filing Prerequisites sheet in the editor
 * (filing-form-container's getEnabledSheets() returns just
 * 'filing-prerequisites' until the user clicks "proceed" into Phase 2), and
 * the remaining 25 sheets have not been converted yet — their slices are
 * empty/default. Running the full registry over them in Phase 1 produced a
 * cache full of false `hasErrors: true` for every empty-but-mandatory sheet
 * (the reported bug). The product rule is: a Phase-1 conversion shows
 * "Review" iff Filing Prerequisites has errors, otherwise "Success" — so
 * validating that one sheet is sufficient and correct.
 */
const PHASE1_SHEET_KEYS: ReadonlyArray<keyof FilingFormState> = [
  SHEET_KEYS.filingPrerequisites,
];

/**
 * Sheets the orchestrator validates while a conversion is in Phase 2.
 *
 * The two filing-info slots are mutually exclusive across phases: Phase 1
 * writes the editable filing-info to role 0 (`filingPrerequisites`) and
 * Phase 2 writes it to role 1 (`filingInfo`) — see PR-D strict phase-aware
 * hydration. In Phase 2 the `filingPrerequisites` slot is therefore an empty
 * stub; validating it emits a phantom `hasErrors: true` that has nothing to
 * do with the user's actual filing (the reported Phase-2 bug). So Phase 2
 * validates every sheet EXCEPT `filingPrerequisites`. (Phase 1 is the mirror:
 * it validates only `filingPrerequisites` and skips `filingInfo`.)
 */
const PHASE2_SHEET_KEYS: ReadonlyArray<keyof FilingFormState> =
  ALL_SHEET_KEYS.filter((key) => key !== SHEET_KEYS.filingPrerequisites);

/**
 * ConversionValidatorService
 *
 * The single API the listing and dashboard pages consume to obtain a
 * conversion's per-sheet validation status. Wraps the read/skip/queue policy
 * so consumers stay one-line ("validate this conversion, tell me when ready").
 *
 * Routing (post Phase 2F):
 *   - In-progress backend status -> emit 'in-progress' immediately, no work.
 *   - Cache hit (complete:true)  -> emit 'cache' immediately, no work.
 *   - Phase 1 cache miss          -> validate ONLY filingPrerequisites
 *                                   (read from localStorage; no MBRS fetch),
 *                                   emit 'phase1-deferred'.
 *   - Cache miss / partial cache -> queued through `cacheMissQueue`,
 *                                   fetched via RetrieveMbrsDataService,
 *                                   validated by the full pure-validator
 *                                   registry, emitted 'computed'.
 *
 * Cache WRITE policy: the cache-miss compute path (reached only when resolving
 * a `PendingValidation` row from the listing/dashboard) is READ-ONLY against
 * the cache — it computes a verdict to derive the conversion-level terminal
 * status but does NOT persist it. The validation cache is written exclusively
 * by the editor's validateLoadedConversion, which runs over the fully-hydrated
 * in-memory FilingFormState and is the authoritative source for the
 * filing-form-container dropdown. Persisting the headless verdict here poisoned
 * that dropdown (almost every sheet ✗, only Filing Info passing). See
 * {@link computeSheetStatuses}.
 *
 * Cache invalidation is owned by ConversionValidationCacheService; the three
 * triggers (submit, logout, SignalR) are wired in their respective services
 * during Phase 1 and do not need to be coordinated through this orchestrator.
 */
@Injectable({ providedIn: 'root' })
export class ConversionValidatorService {
  private readonly cache = inject(ConversionValidationCacheService);
  private readonly registry = inject(PureValidatorRegistry);
  private readonly retrieveMbrsData = inject(RetrieveMbrsDataService);
  private readonly xbrlTransformer = inject(XbrlDataTransformerService);
  private readonly persistence = inject(FilingFormPersistenceService);
  private readonly sheetApplicability = inject(SheetApplicabilityService);

  /**
   * Tail of the cache-miss compute queue. Each cache-miss request is appended
   * here so that compute work runs one-at-a-time (sequential queue, per the
   * approved Phase 1 isolation strategy). Cache hits and the in-progress skip
   * deliberately bypass this — there is no compute to serialise for them.
   *
   * Reused via concatMap so a fresh subscriber rides on the existing chain
   * rather than reordering it. shareReplay(1) on the per-job observable
   * caches the result for any late subscribers attached to the same request.
   */
  private cacheMissQueue: Observable<unknown> = of(null);

  /**
   * Resolve a conversion's per-sheet validation statuses for display in the
   * listing or dashboard.
   *
   * @param conversionId   - Target conversion's id.
   * @param backendStatus  - The status the backend reported alongside the
   *                         listing row. Used only to short-circuit the
   *                         InProgress case; everything else routes through
   *                         the cache because the backend's Review/Success
   *                         payload is currently outdated by design.
   * @param phase          - The conversion's phase as reported by the backend
   *                         listing row (`ConversionData.phase`). When Phase 1,
   *                         the cache-miss compute validates only
   *                         filingPrerequisites. Undefined (pre-rollout or an
   *                         un-tagged row) falls through to the full registry
   *                         pass so real errors are never masked.
   * @returns A cold observable that emits exactly once with the resolved
   *          ConversionValidationResult, then completes.
   */
  validateConversion(
    conversionId: string,
    backendStatus: MbrsStatusEnum,
    phase?: ConversionPhaseEnum
  ): Observable<ConversionValidationResult> {
    if (backendStatus === MbrsStatusEnum.InProgress) {
      return of<ConversionValidationResult>({
        conversionId,
        source: 'in-progress',
        statuses: null,
      });
    }

    const cached = this.cache.get(conversionId);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (conversionId == "INSERT_MFRS_CONVERSION_ID_HERE" || conversionId == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Resolve Conversion Status | Validate Conversion] Cached: ", cached);
    // }

    if (cached && cached.complete) {
      return of<ConversionValidationResult>({
        conversionId,
        source: 'cache',
        statuses: cached.statuses,
      });
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (conversionId == "INSERT_MFRS_CONVERSION_ID_HERE" || conversionId == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Resolve Conversion Status | Validate Conversion] No valid cache found.");
    // }

    return this.enqueueCacheMissCompute(conversionId, phase);
  }

  /**
   * Append a cache-miss compute job to the serial queue. Phase 2's compute
   * branch fetches MBRS data, runs the pure validator registry, and persists
   * the result; the queue serialises so we never have two compute jobs
   * touching backend fetches concurrently.
   */
  private enqueueCacheMissCompute(
    conversionId: string,
    phase?: ConversionPhaseEnum
  ): Observable<ConversionValidationResult> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (conversionId == "INSERT_MFRS_CONVERSION_ID_HERE" || conversionId == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Resolve Conversion Status | Enqueue Cache Missed - Recompute] Phase: ", phase);
    // }
    const job$ = defer(() =>
      from(this.computeForCacheMiss(conversionId, phase))
    ).pipe(shareReplay({ bufferSize: 1, refCount: false }));

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (conversionId == "INSERT_MFRS_CONVERSION_ID_HERE" || conversionId == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Resolve Conversion Status | Enqueue Cache Missed - Recompute] Completed.");
    // }
    this.cacheMissQueue = this.cacheMissQueue.pipe(
      concatMap(() => job$)
    );

    return job$;
  }

  /**
   * Cache-miss compute path (headless listing / dashboard, reached only for a
   * `PendingValidation` row via ConversionStatusResolverService).
   *
   * Fetches the conversion's data and runs every registered validator. It
   * deliberately does NOT persist the result to the validation cache: this
   * verdict is computed from a backend fetch BEFORE the editor hydrates its
   * in-memory state, so it is accurate enough to derive the conversion-level
   * terminal status (the only thing the resolver consumes) but must not become
   * the filing-form-container dropdown's cached truth. Persisting it here
   * surfaced almost every sheet as ✗ (only Filing Info passing) the moment the
   * user opened the conversion. The cache is written exclusively by the editor
   * via validateLoadedConversion. See {@link computeSheetStatuses}.
   *
   * If a sheet in ALL_SHEET_KEYS has no registered validator at runtime
   * (a coding mistake — the registry is meant to cover every key), the
   * runRegistry helper falls back to `{ hasErrors: true }` for that sheet
   * so the listing surfaces the gap visibly rather than silently dropping
   * it. The Phase 2F coverage gate / 'pending-phase2' fallback is gone —
   * the registry roster in PureValidatorRegistry.LIFTED_VALIDATORS is
   * audited at code-review time.
   *
   * Phase 1 short-circuit: when the conversion is in Phase 1 we skip the MBRS
   * fetch entirely (the downstream sheets have no backend data yet) and
   * validate only filingPrerequisites read through the persistence seam
   * (FilingFormPersistenceService / IndexedDB). See {@link PHASE1_SHEET_KEYS}.
   */
  private async computeForCacheMiss(
    conversionId: string,
    phase?: ConversionPhaseEnum
  ): Promise<ConversionValidationResult> {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (conversionId == "INSERT_MFRS_CONVERSION_ID_HERE" || conversionId == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Resolve Conversion Status | Compute For Cache Miss] Phase: ", phase);
    // }
    if (isPhase1({ phase })) {
      const phase1Data = await this.loadPhase1SheetData(conversionId);
      // Compute-only — do NOT persist. See computeSheetStatuses: the headless
      // listing path that resolves a PendingValidation row must not write the
      // validation cache (it would poison the editor's dropdown). The derived
      // statuses are returned solely so the resolver can pick the conversion-
      // level terminal status.
      const statuses = this.computeSheetStatuses(phase1Data, phase);
      return {
        conversionId,
        source: 'phase1-deferred',
        statuses,
      };
    }

    const { sheetDataByStateKey, snapshotByStateKey } =
      await this.loadConversionSheetData(conversionId);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (conversionId == "INSERT_MFRS_CONVERSION_ID_HERE" || conversionId == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Resolve Conversion Status | Compute For Cache Miss] Sheet Data By Key: ", sheetDataByStateKey);
    // }
    // Compute-only — do NOT persist. See computeSheetStatuses: writing this
    // headless, backend-fetched verdict to mbrs-validation-cache-<id> poisoned
    // the filing-form-container dropdown (almost every sheet ✗, only Filing
    // Info passing) once the user opened the conversion. The editor's own
    // runBackgroundValidationCycle -> validateLoadedConversion, running over the
    // fully-hydrated in-memory state, stays the sole authoritative cache writer.
    //
    // Thread the per-sheet N8N snapshot so the calc-diff `Success *` asterisk on
    // the listing diffs recomputed totals against the IMMUTABLE extraction —
    // matching the mounted editor (which threads it via validateLoadedConversion)
    // instead of diffing against the post-recompute persisted slice (which would
    // under-report the asterisk). PO global rule: every `*` on a sum-derived
    // sheet must be a real mismatch vs the N8N baseline.
    const statuses = this.computeSheetStatuses(
      sheetDataByStateKey,
      phase,
      snapshotByStateKey
    );
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (conversionId == "INSERT_MFRS_CONVERSION_ID_HERE" || conversionId == "INSERT_MPERS_CONVERSION_ID_HERE") {
    //   console.log("[On Resolve Conversion Status | Compute For Cache Miss] Statuses: ", statuses);
    // }

    return {
      conversionId,
      source: 'computed',
      statuses,
    };
  }

  /**
   * Build the Phase-1 sheet-data map: just the filingPrerequisites slice read
   * through the persistence seam (FilingFormPersistenceService / IndexedDB —
   * the only sheet validated in Phase 1). Returns an empty Map when the user
   * has never opened the conversion in the editor — the filingPrerequisites
   * validator then runs against `{}` and reports `hasErrors: true`, surfacing
   * the row as "Review" rather than silently passing it as "Success". No MBRS
   * round-trip: the downstream sheets carry no backend data in Phase 1, so
   * fetching them would only feed the false positives this scoping exists to
   * prevent.
   */
  private async loadPhase1SheetData(
    conversionId: string
  ): Promise<Map<keyof FilingFormState, Record<string, any>>> {
    const out = new Map<keyof FilingFormState, Record<string, any>>();
    const filingPrerequisites =
      await this.readFilingPrerequisites(conversionId);
    if (filingPrerequisites) {
      out.set(SHEET_KEYS.filingPrerequisites, filingPrerequisites);
    }
    return out;
  }

  /**
   * Run the registry against pre-loaded sheet data (no backend fetch),
   * persist the result, and return the SheetLevelStatusMap.
   *
   * The editor calls this on conversion entry to populate the sheet
   * selector dropdown's per-sheet status icons (replacing the legacy
   * runBackgroundValidationCycle component-mount loop). Avoids a
   * redundant RetrieveMbrsDataService.fetchMbrsData round-trip because
   * the editor's FilingFormStateService already holds the loaded slices.
   *
   * Returns synchronously — pure validators have no I/O. Cache is written
   * with `complete: true` so subsequent listing reads see the result as
   * authoritative until an explicit invalidation trigger fires.
   *
   * Phase-aware (see {@link resolveValidatedSheetKeys}): Phase 1 validates
   * only `filingPrerequisites` (the sole revealed sheet); Phase 2 validates
   * every sheet except `filingPrerequisites` (its slot is an empty stub in
   * Phase 2 and would otherwise emit a phantom error); unknown phase runs the
   * full registry.
   *
   * @param conversionId           - Target conversion's id.
   * @param sheetDataByStateKey    - Map of FilingFormState slices the
   *                                 editor / caller already holds.
   * @param phase                  - The conversion's phase, used to pick the
   *                                 validated key set.
   * @returns The SheetLevelStatusMap for the validated key set (one entry in
   *          Phase 1, 25 in Phase 2, all 26 for unknown phase).
   */
  validateLoadedConversion(
    conversionId: string,
    sheetDataByStateKey: Map<keyof FilingFormState, Record<string, any>>,
    phase?: ConversionPhaseEnum,
    snapshotByStateKey?: ReadonlyMap<keyof FilingFormState, Record<string, any>>
  ): SheetLevelStatusMap {
    const statuses = this.computeSheetStatuses(
      sheetDataByStateKey,
      phase,
      snapshotByStateKey
    );
    this.cache.set(conversionId, statuses, true);
    return statuses;
  }

  /**
   * Pure compute: build the validation context, pick the phase-appropriate
   * sheet key set, and run the pure-validator registry. Returns the
   * SheetLevelStatusMap WITHOUT persisting it to the validation cache.
   *
   * This is the seam that lets the two callers differ on persistence:
   *  - The editor (and dev preview) go through {@link validateLoadedConversion},
   *    which computes here and then writes the cache — that path runs over the
   *    fully-hydrated in-memory FilingFormState and is therefore the
   *    AUTHORITATIVE source for the filing-form-container dropdown icons and the
   *    listing/dashboard chips on the next visit.
   *  - The headless listing path ({@link computeForCacheMiss}, reached only when
   *    resolving a `PendingValidation` row) calls this directly and deliberately
   *    does NOT persist. Its inputs are a backend fetch (plus localStorage for
   *    filingPrerequisites) assembled BEFORE the editor hydrates, so its
   *    per-sheet verdicts are accurate enough to derive the conversion-level
   *    terminal status (the only thing the resolver needs) but must not become
   *    the dropdown's cached truth — doing so surfaced almost every sheet as ✗
   *    (only Filing Info passing) the moment the user opened the conversion.
   */
  private computeSheetStatuses(
    sheetDataByStateKey: Map<keyof FilingFormState, Record<string, any>>,
    phase?: ConversionPhaseEnum,
    snapshotByStateKey?: ReadonlyMap<keyof FilingFormState, Record<string, any>>
  ): SheetLevelStatusMap {
    const context = buildSheetValidationContext(sheetDataByStateKey, {
      // Thread the N8N baselines so cross-sheet recompute anchors referenced
      // operands to their extraction (global rule — a sub-sheet recompute
      // divergence is a non-blocking warning, not a blocking cross-sheet error).
      crossSheetBaseline: snapshotByStateKey,
    });
    const sheetKeys = this.applyApplicabilityFilter(
      this.resolveValidatedSheetKeys(phase),
      sheetDataByStateKey,
      context,
      phase
    );
    return this.runRegistry(
      sheetDataByStateKey,
      context,
      sheetKeys,
      snapshotByStateKey
    );
  }

  /**
   * Restrict the phase-resolved sheet set to the sheets that ACTUALLY apply to
   * this conversion's permutation — the same `SheetApplicabilityService`
   * decision the mounted editor uses for `getEnabledSheets()`.
   *
   * Why this exists (the phantom `Success *` it closes):
   *   The editor only shows applicable sheets — e.g. Role 3 (stock-exchange) is
   *   enabled ONLY for FS-MFRS / FS-BNM, so an FS-MPERS filing never displays it.
   *   The headless path historically validated the blanket PHASE2 set, so it ran
   *   Role 3 against an EMPTY slice (the backend omits InvolveInSE when it is Not
   *   Applicable). Role 3's three unconditional `isEmpty` schema warnings then
   *   flipped `hasSuggestedChanges`, surfacing a phantom `Success *` on the
   *   listing that the editor (which hides Role 3) never showed. Gating the
   *   headless set by applicability removes that — and any future inapplicable
   *   sheet's leaked warning/error — keeping the listing in parity with the editor.
   *
   * Conservative by design: the filter is applied ONLY when the conversion's
   * `TypeOfSubmission` has resolved. With an unresolved submission type the
   * applicability rules cannot be evaluated safely (an empty type defaults the
   * method-gated monetary sheets OUT), so we fall back to the full phase set —
   * never wrongly dropping an applicable sheet. The nav-gate inputs are forced
   * "unblocked" (`isActiveFilingInfoValid`/`hasMbrsData` true) because the
   * headless path is validating a fetched conversion, not gating UI navigation.
   * `scenarioHiddenRoles` is left empty: scenario hiding only removes MORE sheets,
   * so omitting it keeps this a strict superset and avoids replicating component
   * scenario logic here.
   */
  private applyApplicabilityFilter(
    sheetKeys: ReadonlyArray<keyof FilingFormState>,
    sheetDataByStateKey: Map<keyof FilingFormState, Record<string, any>>,
    context: SheetValidationContext,
    phase?: ConversionPhaseEnum
  ): ReadonlyArray<keyof FilingFormState> {
    const filingInfo =
      context.crossSheetData?.get(SHEET_KEYS.filingInfo) ?? {};
    const typeOfSubmission = (filingInfo[DEI.TypeOfSubmission] as string) ?? '';
    if (!typeOfSubmission) return sheetKeys;

    // Methods live on scope-of-filing in Phase 2 (role 2 is authoritative),
    // falling back PER FIELD to the filing-info slice (which mirrors them via the
    // role-1 passthrough) — so a missing scope slice, or a future transform that
    // drops a simple method field, can never silently blank a method and wrongly
    // exclude an applicable monetary sheet. Same sources the editor reads.
    const scopeData = sheetDataByStateKey.get(SHEET_KEYS.scopeOfFiling) ?? {};
    const read = (slice: Record<string, any>, key: string) =>
      (slice[key] as string) ?? '';
    const readMethod = (key: string) =>
      read(scopeData, key) || read(filingInfo, key);

    const candidateSheets: SheetOption[] = sheetKeys.map((key) => ({
      label: '',
      value: SHEET_TYPE_BY_STATE_KEY[key],
      icon: '',
      description: '',
    }));

    const enabled = new Set<SheetType>(
      this.sheetApplicability
        .computeEnabledSheets({
          sheets: candidateSheets,
          phase: phase ?? null,
          isActiveFilingInfoValid: true,
          hasMbrsData: true,
          isHydratingMbrsData: false,
          typeOfSubmission,
          auditStatus: read(filingInfo, DEI.DisclosureOfFinancialStatementsAuditStatus),
          methodFinancialPosition: readMethod(METHOD_FINANCIAL_POSITION_KEY),
          methodProfitLoss: readMethod(METHOD_PROFIT_LOSS_KEY),
          methodComprehensiveIncome: readMethod(METHOD_COMPREHENSIVE_INCOME_KEY),
          methodCashFlows: readMethod(METHOD_CASH_FLOWS_KEY),
          scenarioHiddenRoles: [],
        })
        .map((option) => option.value)
    );

    return sheetKeys.filter((key) => enabled.has(SHEET_TYPE_BY_STATE_KEY[key]));
  }

  /**
   * Pick the set of sheet keys to validate for a given phase.
   *
   *  - Phase 1  -> {@link PHASE1_SHEET_KEYS} (only `filingPrerequisites`).
   *  - Phase 2  -> {@link PHASE2_SHEET_KEYS} (all sheets except
   *               `filingPrerequisites`, whose role-0 slot is an empty stub
   *               in Phase 2).
   *  - unknown  -> {@link ALL_SHEET_KEYS}. Pre-rollout / not-yet-reported
   *               phase: validate everything rather than guess which
   *               filing-info slot is active, so no real error is masked.
   *               (The backend now ships `phase`, so this path is rare.)
   */
  private resolveValidatedSheetKeys(
    phase?: ConversionPhaseEnum
  ): ReadonlyArray<keyof FilingFormState> {
    if (isPhase1({ phase })) return PHASE1_SHEET_KEYS;
    if (isPhase2({ phase })) return PHASE2_SHEET_KEYS;
    return ALL_SHEET_KEYS;
  }

  /**
   * Fetch the conversion's data from the backend and re-key it from slugs to
   * FilingFormState keys, returning BOTH the working `sheetDataByStateKey` and
   * the immutable N8N `snapshotByStateKey` (the latter drives the calc-diff
   * `Success *` asterisk — see {@link flattenSlugKeyedSheets} and the
   * computeForCacheMiss call site). Sheets the backend did not emit (e.g. an
   * alternative-layout variant the conversion does not use) are left absent from
   * the returned maps; validators receive `{}` for those, which they tolerate.
   *
   * filingPrerequisites is special: it is a hidden config sheet that lives
   * only in the persisted modified-state blob (auto-filled by the backend at
   * conversion time and persisted under the `mbrs-filing-form-modified-<id>`
   * key, now owned by FilingFormPersistenceService / IndexedDB). It is NOT
   * present in the MBRS retrieve payload, so several lifted validators
   * (role 7's hideWhen, role 24's requiredIf, etc.) would otherwise see an
   * empty filingPrerequisites and degrade. Read it through the persistence
   * seam here to keep parity with what the editor sees once a sheet is opened.
   */
  private async loadConversionSheetData(
    conversionId: string
  ): Promise<{
    sheetDataByStateKey: Map<keyof FilingFormState, Record<string, any>>;
    snapshotByStateKey: Map<keyof FilingFormState, Record<string, any>>;
  }> {
    const fetched = await firstValueFrom(this.retrieveMbrsData.fetchMbrsData(conversionId));

    const sheetDataByStateKey = this.flattenSlugKeyedSheets(fetched.sheetData);
    // The immutable N8N extraction (`snapshot`) arrives in the IDENTICAL
    // slug -> nested-DTO shape as the working `sheetData` (see
    // RetrieveMbrsDataService), so flatten it the SAME way. Threading it lets the
    // calc-diff validators surface the `Success *` asterisk by diffing recomputed
    // totals against the raw extraction — the headless mirror of the editor's
    // populateSnapshotFromMbrsData. Absent when the backend omits it (older
    // rows): validators fall back to the persisted slice (prior behaviour).
    const snapshotByStateKey = this.flattenSlugKeyedSheets(fetched.snapshot ?? {});

    const filingPrerequisites = await this.readFilingPrerequisites(conversionId);
    if (filingPrerequisites) {
      sheetDataByStateKey.set(SHEET_KEYS.filingPrerequisites, filingPrerequisites);
    }

    return { sheetDataByStateKey, snapshotByStateKey };
  }

  /**
   * Re-key a slug -> (nested backend DTO) map into FilingFormState keys,
   * flattening each dual-column slice to the editor's flat
   * `qname[Dimension][Period]` shape via transformRoleData. Shared by the working
   * `sheetData` and the immutable `snapshot`, which arrive in the identical shape.
   *
   * Without the flatten, every dual-column lookup (`qname[Dim][Period]`) missed
   * and the sheet falsely reported hasErrors. See STATE_KEY_TO_ROLE_NUMBER + the
   * spec. Slugs not in SLUG_TO_STATE_KEY are silently dropped — backend-only
   * metadata or an unmapped layout variant; a console warning would spam dev
   * consoles for known unmapped variants, so gaps surface via the coverage gate.
   */
  private flattenSlugKeyedSheets(
    slugKeyed: Record<string, any>
  ): Map<keyof FilingFormState, Record<string, any>> {
    const out = new Map<keyof FilingFormState, Record<string, any>>();
    for (const [slug, value] of Object.entries(slugKeyed)) {
      const stateKey = SLUG_TO_STATE_KEY[slug];
      if (!stateKey) continue;
      const roleNumber = STATE_KEY_TO_ROLE_NUMBER[stateKey];
      const slice =
        roleNumber !== undefined
          ? this.xbrlTransformer.transformRoleData(
              roleNumber,
              value as Record<string, any>
            )
          : (value as Record<string, any>);
      out.set(stateKey, slice);
    }
    return out;
  }

  /**
   * Pull the active filing-info slice for a conversion out of the persisted
   * modified-state blob (FilingFormPersistenceService / IndexedDB) where the
   * editor (FilingFormStateService) persists per-conversion modified state,
   * and expose it under the legacy `filingPrerequisites` key validators read.
   *
   * Phase-aware: under PR-D strict hydration, Phase 1 writes only to the
   * `filingPrerequisites` slice (role 0) and Phase 2 writes only to the
   * `filingInfo` slice (role 1). Pick whichever is populated, preferring
   * `filingPrerequisites` when both happen to coexist (mid-migration
   * payloads). This mirrors `FilingFormStateService.getActiveFilingInfoData()`
   * so the dashboard validation path agrees with the editor's own parity-
   * check path (filing-form-state.service.ts:2136 aliases the same data
   * under `filingPrerequisites` for the same reason).
   *
   * Returns null when:
   *   - the user has never opened the conversion in the editor (no entry), or
   *   - both slices are empty/missing.
   *
   * Null causes callers to leave filingPrerequisites absent from the
   * crossSheetData Map. Validators that depend on it must tolerate that
   * absence (the editor itself starts in this state before its valueChanges
   * pipelines fire).
   */
  private async readFilingPrerequisites(
    conversionId: string
  ): Promise<Record<string, any> | null> {
    try {
      const modified = await this.persistence.getModified(conversionId);
      if (!modified) return null;
      const candidates = [(modified as any)?.filingPrerequisites, (modified as any)?.filingInfo];
      for (const slice of candidates) {
        if (slice && typeof slice === 'object' && Object.keys(slice).length > 0) {
          return slice as Record<string, any>;
        }
      }
      return null;
    } catch (error) {
      console.error(
        '[ConversionValidator] failed to read filingPrerequisites from persistence',
        error
      );
      return null;
    }
  }

  // buildContext / resolveActiveFilingInfo were extracted to the pure
  // sheet-validation-context.util so the mounted role components can assemble
  // an identically-shaped SheetValidationContext when they reuse a pure
  // validator's verdict — keeping the headless and mounted paths from
  // drifting. See buildSheetValidationContext (imported above).

  /**
   * Run the registered validators over the loaded payload and assemble a
   * SheetLevelStatusMap for the requested `sheetKeys` (all 26 by default; the
   * Phase-1 caller narrows this to {@link PHASE1_SHEET_KEYS}). The registry is
   * expected to cover every key in ALL_SHEET_KEYS — its roster in
   * PureValidatorRegistry is audited at code-review time. If a validator is
   * unexpectedly missing here, the orchestrator falls back to the cautious
   * default `{ hasErrors: true, hasSuggestedChanges: false }` so the listing
   * surfaces the problem instead of silently dropping the sheet.
   */
  private runRegistry(
    sheetDataByStateKey: Map<keyof FilingFormState, Record<string, any>>,
    context: SheetValidationContext,
    sheetKeys: ReadonlyArray<keyof FilingFormState> = ALL_SHEET_KEYS,
    snapshotByStateKey?: ReadonlyMap<keyof FilingFormState, Record<string, any>>
  ): SheetLevelStatusMap {
    const out: Partial<Record<keyof FilingFormState, SheetLevelStatusData>> = {};

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Conversion Validator | Run Registry] Sheet Keys: ", sheetKeys);
    // console.log("[On Conversion Validator | Run Registry] Sheet Context: ", context);

    for (const sheetKey of sheetKeys) {
      const validator = this.registry.get(sheetKey);
      const data = sheetDataByStateKey.get(sheetKey) ?? {};
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log("[On Conversion Validator | Run Registry] Key: ", sheetKey);
      // console.log("[On Conversion Validator | Run Registry] Data: ", data);

      if (!validator) {
        out[sheetKey] = { hasErrors: true, hasSuggestedChanges: false };
        continue;
      }
      // Per-sheet immutable N8N baseline for the non-blocking Validated* diff.
      // Only spread a fresh context when a snapshot exists for this sheet, so the
      // shared context (and its crossSheetData map) is reused everywhere else.
      const baseline = snapshotByStateKey?.get(sheetKey);
      const perSheetContext: SheetValidationContext = baseline
        ? { ...context, extractedBaseline: baseline }
        : context;
      const result = validator.validate(data, perSheetContext);

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log("[On Conversion Validator | Run Registry] Validation Result: ", result);

      out[sheetKey] = {
        hasErrors: result.hasErrors,
        hasSuggestedChanges: result.hasSuggestedChanges,
      };
    }

    return out as SheetLevelStatusMap;
  }
}
